import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router } from 'react-router-dom'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { reportWebVitals, sendToVercelAnalytics } from '@percent/utility'

import { env } from '@percent/cause-dashboard/env'
import { WithAxios } from '@percent/cause-dashboard/common/HOC/withAxios/WithAxios'
import { logger } from '@percent/cause-dashboard/common/utility/logger'
import { AnalyticsContextController } from '@percent/cause-dashboard/context/analytics/AnalyticsContextController/AnalyticsContextController'
import { AuthContextController } from '@percent/cause-dashboard/context/auth'
import { LemonadeProvider, ErrorBoundaryFallback } from '@percent/lemonade'
import { QueryParamProvider } from 'use-query-params'
import { App } from './app/app-entry'
import { AppProviders } from './app/providers/AppProviders'
import TermlyBanner from './app/providers/TermlyBanner'
import { CountriesProvider } from './context/countries'
import { ServiceContextProvider } from './context/serviceContext/ServiceContextProvider'
import { goodstackPalette } from './theme/theme'
import { MantineProvider } from '@mantine/core'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

if (env.NX_PUBLIC_USE_SENTRY_LOGGER === true) {
  Sentry.init({
    dsn: env.NX_PUBLIC_SENTRY_DSN,
    environment: env.NX_PUBLIC_REACT_APP_ENV,
    autoSessionTracking: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.33
  })
}

const container = document.getElementById('root')
const root = createRoot(container!)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
})

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<ErrorBoundaryFallback />}
      onError={(error: unknown, componentStack: string | undefined, eventId: string) => {
        logger(error as Error)
      }}
    >
      <AppProviders>
        <QueryClientProvider client={queryClient}>
          <Router>
            <MantineProvider>
              {/* TODO - remove custom palette once lemonade theme has been updated */}
              <LemonadeProvider customPalette={goodstackPalette}>
                <AnalyticsContextController
                  logger={logger}
                  writeKey={env.NX_PUBLIC_REACT_APP_SEGMENT_WRITE_KEY}
                  options={{ disableClientPersistence: false }}
                >
                  <QueryParamProvider adapter={ReactRouter6Adapter}>
                    <ServiceContextProvider>
                      <CountriesProvider>
                        <AuthContextController>
                          <TermlyBanner />
                          <WithAxios>
                            <App />
                          </WithAxios>
                        </AuthContextController>
                      </CountriesProvider>
                    </ServiceContextProvider>
                  </QueryParamProvider>
                </AnalyticsContextController>
              </LemonadeProvider>
            </MantineProvider>
          </Router>
        </QueryClientProvider>
      </AppProviders>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)

reportWebVitals(sendToVercelAnalytics)
